/**
 * web/src/screens/Notebook/hooks/opinions.ts — the suggestion/filing opinions
 * seam of useNotebookState (card vncVrGk1hc9i, round two): resolving agent
 * tag suggestions and the hand filing of a card's kind or category. Pure
 * move out of the former hooks.ts — no behaviour change.
 */
import { useState } from 'react';
import { api } from '../../../shell/bridge';
import { CATEGORY_LABEL, type Category, type NotebookItem } from '../types';
import type { NotebookFeed } from './feed';

export function useNotebookOpinions({ busyId, setBusyId, reload, setRowError, flashRowError }: {
  busyId: NotebookFeed['busyId'];
  setBusyId: NotebookFeed['setBusyId'];
  reload: NotebookFeed['reload'];
  setRowError: NotebookFeed['setRowError'];
  flashRowError: NotebookFeed['flashRowError'];
}) {
  const [busySugg, setBusySugg] = useState<string | null>(null);
  const [overriding, setOverriding] = useState<string | null>(null);
  const [overrideText, setOverrideText] = useState('');

  /** The chooser's write (card xNGOA5rZz9LV phase 3, sol review 2026-10-19):
   *  an 'ask' card carries the ranked candidates plus every kind, so a person
   *  can file it into Events, Places, Things, Trips — the homes the legacy
   *  correction dropdown could not reach. 'Filed by hand' is the honest default
   *  why, same convention as retag(); the server clears the band on success, so
   *  the chooser leaves with the question it answered. */
  const fileKind = async (item: NotebookItem, kind: string, subtype: string | null, label: string) => {
    if (busyId) return;
    setBusyId(item.itemId);
    try {
      await api(`/api/notebook/${item.itemId}/kind`, { body: { kind, subtype, reason: `Filed by hand — ${label}` } });
      setRowError(null);
      await reload();
    } catch (e) {
      console.error('[Pear] kind filing failed', e);
      flashRowError(item.itemId, 'That filing didn\'t save — the card is unchanged. Try again.');
    } finally {
      setBusyId(null);
    }
  };

  const retag = async (item: NotebookItem, category: Category) => {
    if (busyId) return;
    const answer = window.prompt(`Why "${CATEGORY_LABEL[category]}"? (shown on the card)`, item.categoryReason || '');
    if (answer === null) return;
    /* Card vPoiTtAn26lI: manual categorisation is the ESSENTIALS route — agent
       filing is Plus, so a free couple files their own items. The reason is
       therefore optional on a hand pick (the route still requires one, by
       contract): an empty answer becomes an honest default instead of an alert
       that blocks the only route a free couple has. */
    const reason = answer.trim() || 'Filed by hand';
    setBusyId(item.itemId);
    try {
      await api(`/api/notebook/${item.itemId}/category`, { body: { category, reason } });
      await reload();
    } catch (e) {
      console.error('[Pear] retag failed', e);
      window.alert('That tag could not be saved — try again.');
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Accept the agent's word, or override it with your own. Either way the
   * decision is written to the same read log the AI screen shows (card
   * ioKcDXGeHnE7) — the UI just posts it and reloads.
   */
  const resolveSuggestion = async (id: string, action: 'accept' | 'override', tag?: string) => {
    if (busySugg) return;
    setBusySugg(id);
    try {
      await api(`/api/notebook/suggestions/${id}`, {
        body: tag ? { action, tag } : { action },
      });
      setOverriding(null);
      setOverrideText('');
      await reload();
    } catch (e) {
      console.error('[Pear] suggestion resolve failed', e);
      window.alert('That suggestion could not be saved — try again.');
    } finally {
      setBusySugg(null);
    }
  };

  return {
    busySugg, setBusySugg, overriding, setOverriding, overrideText, setOverrideText,
    resolveSuggestion, fileKind, retag,
  };
}

export type NotebookOpinions = ReturnType<typeof useNotebookOpinions>;
